"use client"

import { useMemo, useState } from "react"
import { formatDistanceToNow } from "date-fns"

import { asArray } from "@/lib/as-array"
import { useGetJobs, useGetOrgObsUsers, useGetSearch } from "openapi/api/endpoints"
import type { GetOrgObsUsers200Item, GetSearch200Item, Job } from "openapi/api/model"

/** One criterion of a saved search, as 7a's pill: a quiet label and a value. */
export type SearchCriterion = { label: string; value: string }

export type SearchRow = {
  id: string
  /** What the results route is keyed on — the query's hash, not the row id. */
  hash: string
  title: string
  brief?: string
  results?: number
  criteria: SearchCriterion[]
  /** The job this search feeds, when a job names it. */
  feeds?: { jobId: string; title: string; company?: string }
  owner?: { name: string; initials: string }
  /** "2 days ago" — a machine value, so it renders mono. */
  when?: string
}

function initialsOf(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "?"
  )
}

/** A list the API stores as one string, back as words a person reads. */
function listOf(value?: string | null) {
  if (!value) return undefined
  const parts = value
    .split(/\s+OR\s+|,/)
    .map((part) => part.trim().replace(/^"|"$/g, ""))
    .filter(Boolean)
  return parts.length ? parts.join(", ") : undefined
}

/**
 * 7a draws Location, Seniority, Skills and Experience. Two of those four are
 * not on the contract — a saved search stores no seniority band and no years of
 * experience — so the pills are built from what is actually there and the row
 * simply carries fewer of them, rather than four with two invented.
 */
function criteriaOf(search: GetSearch200Item): SearchCriterion[] {
  const location = [search.city, search.region, search.country]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" · ")

  return [
    { label: "Location", value: location },
    { label: "Role", value: search.current_role_title ?? "" },
    { label: "Skills", value: listOf(search.skills) ?? "" },
    { label: "Industry", value: listOf(search.industries) ?? "" },
    { label: "Keywords", value: listOf(search.keyword_query) ?? "" },
    {
      label: "Company",
      value: search.current_company_name ?? "",
    },
  ].filter((criterion): criterion is SearchCriterion => Boolean(criterion.value))
}

/**
 * The saved searches, the job each one feeds, and who saved it.
 *
 * Three endpoints rather than one: `/search` has the queries, `/jobs` says
 * which job names a search as its own, and `/orgObs/users` turns the
 * `created_by` id into a person. Without the last two, 7a's "feeds" line and
 * its owner avatar would be a guess.
 */
export function useSearches() {
  const [search, setSearch] = useState("")
  const [job, setJob] = useState("all")

  const searches = useGetSearch()
  const jobs = useGetJobs()
  const users = useGetOrgObsUsers()

  const all = useMemo(() => asArray<GetSearch200Item>(searches.data), [searches.data])

  /** Jobs, indexed by the search each one says it was built from. */
  const jobBySearch = useMemo(() => {
    const bySearch = new Map<string, Job>()
    for (const entry of asArray<Job>(jobs.data)) {
      if (entry.associated_search_id) bySearch.set(entry.associated_search_id, entry)
    }
    return bySearch
  }, [jobs.data])

  const userById = useMemo(() => {
    const byId = new Map<string, GetOrgObsUsers200Item>()
    for (const entry of asArray<GetOrgObsUsers200Item>(users.data)) {
      if (entry.id) byId.set(entry.id, entry)
    }
    return byId
  }, [users.data])

  const rows = useMemo<SearchRow[]>(() => {
    const needle = search.trim().toLowerCase()

    return all
      .filter((entry) => Boolean(entry.id))
      .map((entry) => {
        const feedsJob = jobBySearch.get(entry.id as string)
        const owner = entry.created_by ? userById.get(entry.created_by) : undefined
        const when = entry.created_at ? new Date(entry.created_at) : null

        return {
          id: entry.id as string,
          hash: entry.normalized_query_hash ?? "",
          // The prompt is the brief; a search saved without a name falls back
          // to it rather than showing an id.
          title: entry.given_name?.trim() || entry.prompt?.trim() || "Untitled search",
          brief: entry.given_name?.trim() ? entry.prompt?.trim() : undefined,
          results: entry.total_result_count ?? undefined,
          criteria: criteriaOf(entry),
          feeds: feedsJob
            ? {
                jobId: feedsJob.id,
                title: feedsJob.title,
                company: feedsJob.company_name,
              }
            : undefined,
          owner: owner?.user
            ? { name: owner.user, initials: initialsOf(owner.user) }
            : undefined,
          when:
            when && !Number.isNaN(when.getTime())
              ? formatDistanceToNow(when, { addSuffix: true })
              : undefined,
        }
      })
      .filter((row) => (job === "all" ? true : row.feeds?.jobId === job))
      .filter((row) => {
        if (!needle) return true
        return [row.title, row.brief, row.feeds?.title, ...row.criteria.map((c) => c.value)]
          .some((field) => (field ?? "").toLowerCase().includes(needle))
      })
  }, [all, job, jobBySearch, search, userById])

  /** Only jobs that actually name a search, so the filter cannot select nothing. */
  const jobOptions = useMemo(
    () =>
      [...jobBySearch.values()]
        .map((entry) => ({ id: entry.id, title: entry.title }))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [jobBySearch],
  )

  return {
    rows,
    jobOptions,
    search,
    setSearch,
    job,
    setJob,
    total: all.length,
    /*
      `/jobs` and `/orgObs/users` are required joins, not decoration: they supply
      every row's owner and linked job, and the job filter's options. Reporting
      ready while either is pending or failed shows rows stripped of facts they
      have, and an empty filter for searches that do feed jobs.
    */
    isLoading: searches.isLoading || jobs.isLoading || users.isLoading,
    isError: searches.isError || jobs.isError || users.isError,
  }
}
